import { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { AlertTriangle, BellRing } from 'lucide-react';
import { toast } from 'sonner';
import { ApiError } from '@/lib/api';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  useAccountInvoiceReminder,
  useClearAccountInvoiceReminder,
  useSetAccountInvoiceReminder,
} from '../hooks/useInvoiceReminder';
import type { InvoiceReminderContact } from '../api';

// Customer-facing text. MUST stay identical to
// apps/frontend/src/atek-core/invoice-reminder/language.json (legacy banner + popup).
const CUSTOMER_TEXT = {
  fr: {
    message: 'Des factures de ce compte sont en souffrance depuis le {date}. Pour tout renseignement, communiquez avec {contact}.',
    fallbackContact: 'ATEK au 1-855-982-2835 #2 ou support@atek.io',
  },
  en: {
    message: 'Invoices for this account have been overdue since {date}. For any information, please contact {contact}.',
    fallbackContact: 'ATEK at 1-855-982-2835 #2 or support@atek.io',
  },
} as const;

type CustomerLanguage = keyof typeof CUSTOMER_TEXT;

// Same output as the legacy banner: "1er septembre 2026" / "15 septembre 2026" /
// "September 1, 2026" (date-fns' own fr ordinal would print "15e").
function formatCustomerDate(value: string, language: CustomerLanguage): string {
  const date = parseISO(value);
  if (language === 'en') return format(date, 'MMMM d, yyyy');
  const day = date.getDate();
  return `${day === 1 ? '1er' : day} ${format(date, 'MMMM yyyy', { locale: fr })}`;
}

function buildCustomerMessage(
  overdueSince: string,
  contact: InvoiceReminderContact | null,
  language: CustomerLanguage,
): string {
  const text = CUSTOMER_TEXT[language];
  const contactText = contact
    ? [contact.name, contact.phone, contact.email].filter(Boolean).join(', ')
    : text.fallbackContact;
  return text.message
    .replace('{date}', formatCustomerDate(overdueSince, language))
    .replace('{contact}', contactText);
}

function todayInMontreal(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Montreal', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

export default function InvoiceReminderCard({ accountId }: { accountId: string }) {
  const { data, isLoading, error } = useAccountInvoiceReminder(accountId);
  const setReminder = useSetAccountInvoiceReminder(accountId);
  const clearReminder = useClearAccountInvoiceReminder(accountId);
  const [date, setDate] = useState('');

  const activeDate = data?.reminder?.overdueSince ?? '';
  useEffect(() => {
    setDate(activeDate);
  }, [activeDate]);

  if (isLoading) return <Skeleton className="h-40" />;
  // Roles that can open the account but not manage reminders (no organisations.modify,
  // or not current ATEK staff) get 403: a quiet note, not an error.
  if (error instanceof ApiError && error.status === 403) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="invoice-reminder-unavailable">
        Only account managers and admins can manage the overdue invoice reminder.
      </p>
    );
  }
  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-4 text-sm text-destructive">
          Could not load the overdue invoice reminder.
        </CardContent>
      </Card>
    );
  }

  const { reminder, contact, coverage } = data;
  const today = todayInMontreal();
  const dateIsValid = /^\d{4}-\d{2}-\d{2}$/.test(date) && date <= today;
  const busy = setReminder.isPending || clearReminder.isPending;
  const inputId = `invoice-reminder-date-${accountId}`;

  const save = () => {
    setReminder.mutate(date, {
      onSuccess: () => toast.success(reminder ? 'Reminder date updated' : 'Reminder turned on'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not save the reminder'),
    });
  };

  const turnOff = () => {
    clearReminder.mutate(undefined, {
      onSuccess: () => toast.success('Reminder turned off'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not turn off the reminder'),
    });
  };

  return (
    <Card className={reminder ? 'border-red-300' : undefined} data-testid="invoice-reminder-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <BellRing className="h-4 w-4" aria-hidden="true" />
              Overdue invoice reminder
            </CardTitle>
            <CardDescription>
              {reminder
                ? 'Customers see a red banner on every page and a popup every 15 minutes.'
                : 'Off — customers see nothing.'}
            </CardDescription>
          </div>
          {reminder && (
            <Badge variant="destructive">Active since {formatCustomerDate(reminder.overdueSince, 'en')}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!contact && (
          <p className="flex items-center gap-2 text-sm text-amber-700">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            Add a contractual manager in Key Contacts (Overview tab) before turning this on.
          </p>
        )}

        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor={inputId}>Overdue since</Label>
            <Input
              id={inputId}
              type="date"
              value={date}
              max={today}
              onChange={(e) => setDate(e.target.value)}
              className="w-44"
              disabled={busy}
            />
          </div>
          {reminder ? (
            <>
              <Button onClick={save} disabled={busy || !dateIsValid || date === reminder.overdueSince}>
                Change date
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" disabled={busy}>Turn off</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Turn off the overdue invoice reminder?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Customers of this account stop seeing the banner and the popup within 15 minutes.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={turnOff}>Turn off reminder</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          ) : (
            <Button variant="destructive" onClick={save} disabled={busy || !dateIsValid || !contact}>
              Turn on
            </Button>
          )}
        </div>

        {dateIsValid && (
          <div className="grid gap-2 md:grid-cols-2">
            {(['fr', 'en'] as const).map((language) => (
              <div
                key={language}
                className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-900"
                data-testid={`invoice-reminder-preview-${language}`}
              >
                {buildCustomerMessage(date, contact, language)}
              </div>
            ))}
          </div>
        )}

        {coverage.usergroups.length > 0 ? (
          <p className="text-sm" data-testid="invoice-reminder-coverage">
            Shown to {coverage.usergroups.length} usergroup{coverage.usergroups.length === 1 ? '' : 's'}:{' '}
            {coverage.usergroups.map((group) => group.name).join(', ')} — plus full admins of their
            {' '}organisation{coverage.organisationIds.length === 1 ? '' : 's'}.
          </p>
        ) : (
          <p className="text-sm text-amber-700" data-testid="invoice-reminder-coverage">
            No usergroup is assigned to this account in PostgreSQL — nobody will see the reminder.
            {' '}Open the Users tab to sync, or remap usergroups.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
